import { styled } from "styled-components";
import { Heading, Spinner, Text, Box, VStack, Select, SimpleGrid, Button, HStack, useDisclosure } from "@chakra-ui/react";
import { ExternalLinkIcon, AddIcon, InfoIcon, ViewIcon } from "@chakra-ui/icons";
import { AmountChart } from "./charts/AmountChart";
import { KPICard } from "./charts/KPICard";
import { CampaignProgressBar } from "./charts/CampaignProgressBar";
import { DonationBreakdownChart } from "./charts/DonationBreakdownChart";
import { TopDonorsTable } from "./charts/TopDonorsTable";
import { CampaignBuilderModal } from "./modals/CampaignBuilderModal";
import { SocialShareModal } from "./modals/SocialShareModal";
import { useEffect, useState, useRef } from "react";
import {
  GetClientAnalyticsResponse,
  getClientAnalytics,
  ClientAnalyticsCampaign,
} from "../../api/clientUser/getClientAnalytics";
import { useAuth } from "../../hooks/useAuth";
import { Show } from "../Show";
import { FaDollarSign, FaUsers, FaChartLine, FaGift } from "react-icons/fa";
import { ScheduledMessagesTable } from "./ScheduledMessagesTable";
import {
  ScheduledMessage,
  getCampaignScheduledMessages,
} from "../../api/clientUser/getCampaignScheduledMessages";

const Content = styled.div`
  width: 100%;
`;

export const Analytics = () => {
  const { accessToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const { isOpen: isBuilderOpen, onOpen: onBuilderOpen, onClose: onBuilderClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const [analyticsData, setAnalyticsData] =
    useState<GetClientAnalyticsResponse>({
      amounts: [],
      campaigns: [],
      kpis: {
        totalRaised: 0,
        totalDonors: 0,
        averageDonation: 0,
        conversionRate: 0,
        matchingGifts: 0,
      },
      donationBreakdown: {
        oneTime: 0,
        recurring: 0,
        anonymous: 0,
        named: 0,
        byTier: [],
      },
      paymentStatus: {
        completed: 0,
        pending: 0,
        failed: 0,
      },
      fees: {
        total: 0,
        net: 0,
      },
      topDonors: [],
      campaignProgress: null,
    });
  const [amountTimeFrame, setAmountTimeFrame] = useState<"day" | "month" | "year">("day");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("all");
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<ClientAnalyticsCampaign[]>([]);
  const [isLoadingChartData, setIsLoadingChartData] = useState(false);
  const campaignsLoadedRef = useRef(false);
  const previousCampaignRef = useRef<string>("all");

  // Fetch campaigns list and initial analytics ONCE on mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!accessToken) return;
      setIsLoadingChartData(true);
      const analytics = await getClientAnalytics(accessToken, undefined, amountTimeFrame);
      setAnalyticsData(analytics);
      // Store the full campaigns list for the dropdown
      setAllCampaigns(analytics.campaigns);
      campaignsLoadedRef.current = true;
      setIsProcessing(false);
      setIsLoadingChartData(false);
    };
    fetchCampaigns();
  }, [accessToken, amountTimeFrame]);

  // Fetch analytics data when campaign selection changes
  useEffect(() => {
    // Only run if campaigns have loaded and selection actually changed
    if (!campaignsLoadedRef.current || previousCampaignRef.current === selectedCampaignId) {
      return;
    }

    previousCampaignRef.current = selectedCampaignId;

    if (!accessToken) return;

    setIsProcessing(true);
    const fetchAnalytics = async () => {
      // If "All Campaigns" selected, fetch all
      if (selectedCampaignId === "all") {
        const analytics = await getClientAnalytics(accessToken, undefined, amountTimeFrame);
        setAnalyticsData(analytics ?? []);
        setScheduledMessages([]);
        setIsProcessing(false);
        return;
      }

      // Fetch for specific campaign by ID
      const analytics = await getClientAnalytics(accessToken, selectedCampaignId, amountTimeFrame);
      setAnalyticsData(analytics ?? []);

      // Fetch scheduled messages for the specific campaign
      const messages = await getCampaignScheduledMessages(accessToken, selectedCampaignId);
      setScheduledMessages(messages);

      setIsProcessing(false);
    };
    fetchAnalytics();
  }, [accessToken, selectedCampaignId, amountTimeFrame]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  const selectedCampaign = allCampaigns.find((c) => c.id === selectedCampaignId);

  return (
    <Content>
      <VStack align="start" spacing={6}>
        <Box width="100%">
          <HStack justify="space-between" align="start" mb={4}>
            <Box>
              <Heading
                size="2xl"
                color="#1A202C"
                fontWeight="800"
                mb={2}
              >
                Campaigns
              </Heading>
              <Text fontSize="lg" color="gray.600" fontWeight="500">
                Track your fundraising performance and donor engagement.
              </Text>
              <HStack spacing={1} mt={1}>
                <InfoIcon boxSize={3} color="blue.400" />
                <Text fontSize="xs" color="gray.500">
                  Data refreshes every 5 minutes
                </Text>
              </HStack>
            </Box>
            <HStack spacing={3}>
              {selectedCampaign && (
                <>
                  <Button
                    leftIcon={<ViewIcon />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => window.open(selectedCampaign.shortUrl, "_blank")}
                    size="md"
                  >
                    View Campaign
                  </Button>
                  <Button
                    leftIcon={<ExternalLinkIcon />}
                    colorScheme="green"
                    onClick={onShareOpen}
                    size="md"
                  >
                    Social Share
                  </Button>
                </>
              )}
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                onClick={onBuilderOpen}
                size="md"
              >
                Campaign Builder
              </Button>
            </HStack>
          </HStack>
        </Box>

        <Box width="100%">
          <Select
            value={selectedCampaignId}
            onChange={(e) => setSelectedCampaignId(e.target.value)}
            size="lg"
            maxW="300px"
            borderRadius="12px"
            focusBorderColor="#339CFF"
            fontWeight="600"
            color="#1A202C"
            isDisabled={isProcessing}
          >
            <option key="all" value="all">All Campaigns</option>
            {allCampaigns.map((campaign) => {
              const date = new Date(campaign.createdAt);
              const formattedDate = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
              const displayName = `${campaign.name} (${formattedDate})`;

              return (
                <option key={campaign.id} value={campaign.id}>
                  {displayName}
                </option>
              );
            })}
          </Select>
        </Box>

        <Show
          when={!isProcessing}
          whenFalseContent={
            <Box width="100%" textAlign="center" py={12}>
              <Spinner color="#339CFF" size="xl" />
            </Box>
          }
        >

          {/* Campaign Progress - Show only when a specific campaign is selected */}
          {analyticsData.campaignProgress && (
            <Box width="100%">
              <CampaignProgressBar progress={analyticsData.campaignProgress} />
            </Box>
          )}

          {/* KPI Cards */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} width="100%">
            <KPICard
              label="Total Raised"
              value={formatCurrency(analyticsData.kpis?.totalRaised || 0)}
              icon={FaDollarSign}
              color="#00C851"
              bgGradient="linear(to-br, #00C85120, transparent)"
            />
            <KPICard
              label="Matching Gifts"
              value={formatCurrency(analyticsData.kpis?.matchingGifts || 0)}
              icon={FaGift}
              color="#FF6B35"
              bgGradient="linear(to-br, #FF6B3520, transparent)"
            />
            <KPICard
              label="Total Donors"
              value={analyticsData.kpis?.totalDonors || 0}
              helpText={`${analyticsData.kpis?.conversionRate || 0}% conversion`}
              icon={FaUsers}
              color="#339CFF"
              bgGradient="linear(to-br, #339CFF20, transparent)"
            />
            <KPICard
              label="Avg Donation"
              value={formatCurrency(analyticsData.kpis?.averageDonation || 0)}
              icon={FaChartLine}
              color="#9333EA"
              bgGradient="linear(to-br, #9333EA20, transparent)"
            />
          </SimpleGrid>

          {/* Charts Grid */}
          <Box width="100%">
            <AmountChart
              data={analyticsData.amounts}
              timeFrame={amountTimeFrame}
              onTimeFrameChange={setAmountTimeFrame}
              isLoading={isLoadingChartData}
            />
          </Box>

          {/* Breakdown and Top Donors */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} width="100%">
            {analyticsData.donationBreakdown && (
              <DonationBreakdownChart breakdown={analyticsData.donationBreakdown} />
            )}
            {analyticsData.topDonors && (
              <TopDonorsTable donors={analyticsData.topDonors} />
            )}
          </SimpleGrid>

          {/* Scheduled Messages - Show only when a specific campaign is selected */}
          {selectedCampaign && (
            <ScheduledMessagesTable messages={scheduledMessages} />
          )}
        </Show>
      </VStack>

      {/* Modals */}
      <CampaignBuilderModal isOpen={isBuilderOpen} onClose={onBuilderClose} />
      {selectedCampaign && (
        <SocialShareModal
          isOpen={isShareOpen}
          onClose={onShareClose}
          campaignId={selectedCampaign.id}
          campaignName={selectedCampaign.name}
          shortUrl={selectedCampaign.shortUrl}
        />
      )}
    </Content>
  );
};
